import { useEffect, useState, useRef } from "react";
import { Trophy, Keyboard } from "lucide-react";
import { Button } from "../ui/button";

export function GameTyping({ match, currentUserId, onProgress, onLeave }) {
    const state = match.state;
    const me = match.players.find((p) => p.userId === String(currentUserId));
    const opp = match.players.find((p) => p.userId !== String(currentUserId));
    const paragraph = state.paragraph || "";

    const myProgress = state.progress?.[me.userId] || 0;
    const oppProgress = state.progress?.[opp.userId] || 0;
    const myProgressPct = paragraph.length ? Math.round((myProgress / paragraph.length) * 100) : 0;
    const oppProgressPct = paragraph.length ? Math.round((oppProgress / paragraph.length) * 100) : 0;

    const [typed, setTyped] = useState("");
    const lastEmit = useRef(0);
    const textareaRef = useRef(null);

    // Auto-focus the input
    useEffect(() => {
        if (textareaRef.current && !state.winner) textareaRef.current.focus();
    }, [state.winner]);

    const handleChange = (e) => {
        const val = e.target.value;
        if (val.length > paragraph.length) return;
        setTyped(val);

        // Compute correct count
        let correct = 0;
        for (let i = 0; i < val.length; i++) {
            if (val[i] === paragraph[i]) correct++;
        }

        // Throttle to 5 emits/sec
        const now = Date.now();
        if (now - lastEmit.current > 200 || val.length === paragraph.length) {
            lastEmit.current = now;
            onProgress({ progress: val.length, correct });
        }
    };

    return (
        <div className="flex flex-col items-center w-full">
            {/* Progress bars */}
            <div className="w-full max-w-2xl space-y-2 mb-4">
                <ProgressRow name={`${me.userName} (you)`} pct={myProgressPct} color="bg-sky-500" />
                <ProgressRow name={opp.userName} pct={oppProgressPct} color="bg-gray-400" />
            </div>

            {/* Paragraph (with per-char coloring) */}
            <div className="w-full max-w-2xl bg-white border border-gray-200 rounded-lg p-4 mb-3 font-mono text-base leading-relaxed shadow-sm">
                {paragraph.split("").map((ch, i) => {
                    let cls = "text-gray-400";
                    if (i < typed.length) {
                        cls = typed[i] === ch ? "text-emerald-700 bg-emerald-50" : "text-red-700 bg-red-100";
                    } else if (i === typed.length) {
                        cls = "text-gray-900 underline decoration-2 decoration-sky-500";
                    }
                    return (
                        <span key={i} className={cls}>
                            {ch === " " && i === typed.length ? "·" : ch}
                        </span>
                    );
                })}
            </div>

            {/* Input */}
            <textarea
                ref={textareaRef}
                value={typed}
                onChange={handleChange}
                disabled={!!state.winner}
                rows={2}
                placeholder="Start typing here..."
                className="w-full max-w-2xl px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50"
            />

            <p className="text-[11px] text-gray-400 mt-2 flex items-center gap-1">
                <Keyboard className="h-3 w-3" />
                {myProgressPct}% complete · {state.correct?.[me.userId] || 0}/{paragraph.length} correct
            </p>

            {state.winner && (
                <EndCard
                    winner={state.winner}
                    currentUserId={currentUserId}
                    onLeave={onLeave}
                />
            )}
        </div>
    );
}

function ProgressRow({ name, pct, color }) {
    return (
        <div>
            <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-medium text-gray-700 truncate">{name}</span>
                <span className="text-gray-500 tabular-nums">{pct}%</span>
            </div>
            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                    className={`h-full ${color} transition-all duration-150`}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

function EndCard({ winner, currentUserId, onLeave }) {
    const youWon = winner === String(currentUserId);
    return (
        <div className={`mt-4 px-4 py-3 rounded-lg border text-center max-w-md ${
            youWon ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
        }`}>
            <Trophy className={`h-6 w-6 mx-auto mb-1 ${youWon ? "text-emerald-600" : "text-red-500"}`} />
            <div className={`text-sm font-bold ${youWon ? "text-emerald-800" : "text-red-700"}`}>
                {youWon ? "You won the race!" : "You lost"}
            </div>
            <Button size="sm" variant="outline" onClick={onLeave} className="mt-2">Back to lobby</Button>
        </div>
    );
}
